/**
 * Demo-data mode.
 *
 * The platform ships with an illustrative demo dataset (the "Antarctica" sample
 * portfolio) hardcoded across the frontend. This module provides a single
 * on/off switch so a real client can be given a blank slate that awaits the
 * ingestion of their own data.
 *
 * Source of truth: the admin-controlled `demoDataEnabled` flag on
 * `GET/PATCH /settings/platform` (persisted in AppGlobalSettings). It is
 * mirrored into localStorage so it can be read *synchronously* at module-import
 * time — which is required because many pages compute derived demo data at
 * module scope. Because of that, flipping the toggle triggers a one-time reload
 * so those module-scope constants re-evaluate against the new flag.
 */
import { create } from 'zustand'
import api from '@/services/api'

const STORAGE_KEY = 'cmos_demo_data'

/**
 * Cache written by DemoDataContext after a successful load of
 * `/settings/demo-data`. Read here so a single master switch silences every
 * demo surface regardless of which mechanism a page happens to use.
 */
const MASTER_CACHE_KEY = 'cmos_demo_settings'

/** Synchronous read, safe to call at module-import time. Default: OFF.
 *
 * There are two demo mechanisms in the app: this one (`/settings/platform` →
 * `demoDataEnabled`) and DemoDataContext (`/settings/demo-data` →
 * `masterEnabled`). The Super Admin "Demo Data" tab drives the latter, so
 * turning demo data off there left the pages backed by this module — Intake,
 * CRM, Documents, News — still showing sample brokers and deals.
 *
 * The master switch now wins: if it is off, it is off everywhere. Reading the
 * cache rather than calling the store avoids a reload war between the two
 * hydration paths, and keeps this function synchronous for module-scope use.
 */
export function demoEnabled(): boolean {
  try {
    const cached = localStorage.getItem(MASTER_CACHE_KEY)
    if (cached && JSON.parse(cached)?.masterEnabled === false) {
      return false
    }
    // An absent master cache falls through to the local setting, and an
    // absent local setting is OFF (MOD-31). This used to read `!== 'off'`, so
    // a fresh browser — nothing cached yet — painted the sample dataset on a
    // live account until the server answered and the page reloaded.
    // `hydrateFromServer` writes STORAGE_KEY from the server's answer (now the
    // caller's effective flag), so a demo account still turns demo on: it
    // reloads once after the first answer instead of a live account flashing.
    return localStorage.getItem(STORAGE_KEY) === 'on'
  } catch {
    // Fail CLOSED. This returned true, so a paint that could not reach
    // localStorage — private browsing, a storage exception, the first frame
    // before the cache is warm — showed demo figures on a live deal screen.
    // Sample data appearing by accident is indistinguishable from parsed data,
    // and those screens are how extraction accuracy is judged.
    return false
  }
}

/**
 * What every module-scope `withDemo()` constant in the app was evaluated
 * against. Frozen at bundle evaluation, which is the whole problem it exists to
 * expose: a page that computes its demo dataset at module scope cannot change
 * its mind later in the session, so something has to notice the disagreement
 * and reload once. `DemoDataProvider` is the only place that knows the account,
 * so it is the one that decides.
 */
export const DEMO_AT_MODULE_LOAD = demoEnabled()

function writeLocal(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off')
  } catch {
    /* localStorage unavailable — fall back to in-memory default */
  }
}

/**
 * Forget which demo scope this browser was in.
 *
 * Both mirrors of the flag live in localStorage and the store, and neither knows
 * whose answer it holds. When the signed-in identity changes (sign-out, a
 * different account on the same tab) the previous account's answer must not be
 * the next account's starting point: with it still cached, a live account that
 * followed a demo one read "demo on" until the server answered. Back to the
 * fail-closed state: demo off, not yet reconciled with the server.
 */
export function resetDemoScope(): void {
  try {
    localStorage.removeItem(MASTER_CACHE_KEY)
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* localStorage unavailable — there is no cache to clear */
  }
  useDemoStore.setState({ enabled: false, hydrated: false })
}

/**
 * Whether one module's sample content may show: the master answer above AND
 * that module's own switch on the Super Admin "Demo Data" tab.
 *
 * `withDemo()` used to read the master switch only, so turning one module's
 * demo data off (Asset Management, Portfolio Analytics, Automation, CRM)
 * changed nothing on that module while demo mode was on overall: the
 * per-module switch governed only the pages that read the book through
 * `useModuleLoans`/`useAntarcticaDeals`. A module the cached settings do not
 * mention keeps the master answer (the server's default for a module is on).
 */
export function demoEnabledFor(moduleId: string): boolean {
  if (!demoEnabled()) return false
  try {
    const cached = localStorage.getItem(MASTER_CACHE_KEY)
    const modules = cached ? JSON.parse(cached)?.modules : null
    if (modules && typeof modules === 'object' && moduleId in modules) {
      return Boolean((modules as Record<string, unknown>)[moduleId])
    }
    return true
  } catch {
    return false
  }
}

/**
 * Returns `demoValue` when demo mode is on, otherwise `blankValue`.
 * Use this to gate any hardcoded demo dataset so it collapses to an
 * empty/blank slate when demo mode is off.
 *
 * Pass the module the content belongs to so that module's own demo switch
 * governs it as well. Call it where the page renders, not at module scope: a
 * module-scope constant is frozen at bundle evaluation and cannot follow a
 * switch flipped later in the session.
 */
export function withDemo<T>(demoValue: T, blankValue: T, moduleId?: string): T {
  const on = moduleId ? demoEnabledFor(moduleId) : demoEnabled()
  return on ? demoValue : blankValue
}

interface DemoState {
  /** Reactive mirror of the synchronous flag (for component rendering). */
  enabled: boolean
  /** Whether we've reconciled with the server at least once this session. */
  hydrated: boolean
  /** Pull the authoritative flag from the server; reloads once if it changed. */
  hydrateFromServer: () => Promise<void>
  /** Admin action: persist the new value server-side, then reload. */
  setEnabled: (enabled: boolean) => Promise<void>
}

export const useDemoStore = create<DemoState>((set) => ({
  enabled: demoEnabled(),
  hydrated: false,

  hydrateFromServer: async () => {
    try {
      const res = await api.get<{ data: Record<string, unknown> }>('/settings/platform')
      const flag = res.data?.data?.demoDataEnabled
      if (typeof flag === 'boolean') {
        // Compare *effective* visibility (master cache ∪ platform storage), not the
        // raw platform flag. demoEnabled() short-circuits to false when the Super
        // Admin master switch cache is off — so comparing `flag !== demoEnabled()`
        // while writeLocal only updates STORAGE_KEY left those two forever
        // disagreeing and Layout's hydrate effect reloaded the page on every mount.
        const previousEffective = demoEnabled()
        writeLocal(flag)
        const nextEffective = demoEnabled()
        set({ enabled: nextEffective, hydrated: true })
        // Module-scope demo constants were already evaluated with the old flag —
        // reload once so the whole app reflects the new *effective* value.
        if (nextEffective !== previousEffective) {
          window.location.reload()
        }
      } else {
        set({ hydrated: true })
      }
    } catch {
      // Offline / not yet authenticated — keep the locally cached value.
      set({ hydrated: true })
    }
  },

  setEnabled: async (enabled: boolean) => {
    writeLocal(enabled)
    set({ enabled })
    try {
      await api.patch('/settings/platform', { data: { demoDataEnabled: enabled } })
    } catch {
      /* surfaced by the caller via its own try/catch if needed */
    }
    // Reload so every module-scope demo dataset re-evaluates.
    window.location.reload()
  },
}))

/** Reactive hook for components. */
export function useDemoEnabled(): boolean {
  return useDemoStore((s) => s.enabled)
}
